import React from "react";

import ParseoLogo from "../../shared/ui/ParseoLogo";

/**
 * Parseo-branded progress header for the patient onboarding flow — mirrors the
 * physician onboarding header (logo + "Parseo Health" + a numbered stepper).
 *
 * Steps mirror the existing PatientConfirmDetails stepper so the whole patient
 * journey reads as one 1-2-3 progression:
 *   0 Confirm Details — PatientConfirmDetails (demographics + insurance)
 *   1 Your Form       — BridgeQuestionnaire (the conditional "few quick questions")
 *   2 Review & Sign   — BridgeFieldsForm review of the filled PDF + sign/submit
 *
 * Width-agnostic: the parent screen controls max-width/centering; the stepper
 * wraps on narrow viewports (flexWrap) rather than overflowing.
 */
interface PatientStep {
  id: string;
  title: string;
}

export const PATIENT_STEPS: PatientStep[] = [
  { id: "details", title: "Confirm Details" },
  { id: "form", title: "Your Form" },
  { id: "sign", title: "Review & Sign" },
];

interface PatientStepHeaderProps {
  currentStep?: number;
  subtitle?: string;
  style?: React.CSSProperties;
}

const C = {
  ink: "#182a33",
  muted: "#5b6e7c",
  done: "#6b9c8e",
  future: "#e6dccb",
  connector: "#d8c9b6",
};

export default function PatientStepHeader({ currentStep = 0, subtitle, style }: PatientStepHeaderProps) {
  return (
    <div style={{ width: "100%", marginBottom: 20, ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <ParseoLogo size={34} />
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>
            Parseo Health
          </div>
          {subtitle && (
            <div style={{ fontSize: 13, color: C.muted }}>{subtitle}</div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {PATIENT_STEPS.map((s, i) => (
          <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0,
                backgroundColor:
                  i < currentStep ? C.done : i === currentStep ? C.ink : C.future,
                color: i <= currentStep ? "#fff" : C.muted,
              }}
            >
              {i < currentStep ? "✓" : i + 1}
            </div>
            <span
              style={{
                fontSize: 13,
                fontWeight: i === currentStep ? 600 : 400,
                color: i === currentStep ? C.ink : C.muted,
              }}
            >
              {s.title}
            </span>
            {i < PATIENT_STEPS.length - 1 && (
              <div style={{ width: 20, height: 1, backgroundColor: C.connector }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
